import styles from './TextV2.scss';

import cx from 'classnames';
import Handlebars from 'handlebars';
import React, { useMemo } from 'react';

import { IMarkdownWrapperProps, MarkdownWrapper } from 'src/components/MarkdownWrapper/MarkdownWrapper';
import { useShopTemplateContext } from 'src/components/ShopTemplateContext/ShopTemplateContext';
import { useDCSText } from 'src/hooks/useDCSText';
import { FlatSysV2 } from 'src/services/cms/typesUtil';
import { getEntryDataAttributes } from 'src/utils/GetEntryDataAttributes';
import { generateId } from 'src/utils/IdGeneration';

export type TextV2Variant =
  | 'h1'
  | 'h1-medium'
  | 'h1-bold'
  | 'h2'
  | 'h2-medium'
  | 'h2-bold'
  | 'h3'
  | 'h3-medium'
  | 'h3-bold'
  | 'h4'
  | 'h4-medium'
  | 'h4-bold'
  | 'h5'
  | 'h5-medium'
  | 'h5-bold'
  | 'body'
  | 'body-medium'
  | 'body-bold'
  | 'body-small'
  | 'body-small-bold'
  | 'eyebrow'
  | 'small'
  | 'label'
  | 'nav'
  | 'sub-nav'
  | 'text-link';

export interface TextV2Props extends React.AllHTMLAttributes<HTMLElement> {
  value?: string;
  tagName?: keyof React.ReactHTML;
  entry?: FlatSysV2 & { field?: string };
  isMarkdown?: boolean;
  templateData?: {};
  variant?: TextV2Variant;
  hasId?: boolean;
}

export const TextTag = React.forwardRef<HTMLElement, Omit<TextV2Props, 'tagName'> & { tagName: keyof React.ReactHTML }>(
  ({ tagName, ...props }, ref) => React.createElement(tagName, { ref, ...props })
);

export const TextV2 = React.forwardRef<HTMLElement, TextV2Props & Omit<IMarkdownWrapperProps<HTMLElement>, 'tagName'>>(({
  value,
  children,
  entry,
  isMarkdown,
  templateData,
  tagName = 'span',
  className,
  variant,
  hasId,
  ...props
}, ref) => {
  if (!value) return null;

  const shopTemplate = useShopTemplateContext();
  const text = useDCSText(value);

  const markdownText = useMemo(() => {
    const template = Handlebars.compile(text);
    return template({ shop: shopTemplate, ...templateData });
  }, [text, shopTemplate, templateData]);

  const variantClassName = variant && variantClassNames[variant];
  const id = hasId ? generateId(value) : undefined;

  if (isMarkdown) {
    return (
      <MarkdownWrapper
        id={id}
        source={markdownText}
        innerRef={ref}
        tagName={tagName}
        className={cx(variantClassName, styles.withMarkdown, className)}
        {...getEntryDataAttributes(entry)}
        {...props}
      />
    );
  }

  return (
    <TextTag
      id={id}
      ref={ref}
      tagName={tagName}
      className={cx(variantClassName, className)}
      {...getEntryDataAttributes(entry)}
      {...props}
    >
      {/* Bringing the hyphenated-word to the next line instead of breaking it, by replacing hypen(-) with non-breakable hyphen. */}
      {String(text).replace(/-/g, '\u2011')}
      {children}
    </TextTag>
  );
});

TextV2.displayName = 'TextV2';

export const variantClassNames: Record<TextV2Variant, string> = {
  ['h1']: styles.h1,
  ['h1-medium']: styles.h1Medium,
  ['h1-bold']: styles.h1Bold,
  ['h2']: styles.h2,
  ['h2-medium']: styles.h2Medium,
  ['h2-bold']: styles.h2Bold,
  ['h3']: styles.h3,
  ['h3-medium']: styles.h3Medium,
  ['h3-bold']: styles.h3Bold,
  ['h4']: styles.h4,
  ['h4-medium']: styles.h4Medium,
  ['h4-bold']: styles.h4Bold,
  ['h5']: styles.h5,
  ['h5-medium']: styles.h5Medium,
  ['h5-bold']: styles.h5Bold,
  ['body']: styles.body,
  ['body-medium']: styles.bodyMedium,
  ['body-bold']: styles.bodyBold,
  ['body-small']: styles.bodySmall,
  ['body-small-bold']: styles.bodySmallBold,
  ['eyebrow']: styles.eyebrow,
  ['small']: styles.small,
  ['label']: styles.label,
  ['nav']: styles.nav,
  ['sub-nav']: styles.subNav,
  ['text-link']: styles.textLink,
};
